import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import LogoOrganizacion from '../components/LogoOrganizacion';

const LoginDashboardOTP = () => {
  const [step, setStep] = useState(1); // 1: credentials, 2: OTP
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [userId, setUserId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error('Por favor ingrese usuario y contraseña');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000'}/dashboard/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: username.trim(),
          password: password.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error en la autenticación');
      }

      // Si no requiere OTP, login directo
      if (!data.requires_otp) {
        localStorage.setItem('dashboardToken', data.access_token);
        toast.success('Acceso autorizado');
        navigate('/dashboard');
        return;
      }

      // Si requiere OTP, continuar al paso 2
      setUserId(data.user_id);
      setMaskedEmail(data.message.split('enviado a ')[1] || 'su email registrado');
      setStep(2);
      toast.success(data.message);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    
    if (!otpCode.trim()) {
      toast.error('Por favor ingrese el código OTP');
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8000'}/dashboard/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          otp_code: otpCode.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Código OTP inválido');
      }

      localStorage.setItem('dashboardToken', data.access_token);
      toast.success('Autenticación OTP exitosa');
      navigate('/dashboard');
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      await handleCredentialsSubmit(new Event('submit'));
      toast.info('Código OTP reenviado');
    } catch (error) {
      toast.error('Error al reenviar código OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setOtpCode('');
    setUserId('');
    setMaskedEmail('');
  };

  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="flex justify-center mb-6">
              <LogoOrganizacion className="h-16 w-auto" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Verificación OTP
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Ingrese el código enviado a {maskedEmail}
            </p>
          </div>
          
          <form className="mt-8 space-y-6" onSubmit={handleOTPSubmit}>
            <div>
              <label htmlFor="otpCode" className="sr-only">
                Código OTP
              </label>
              <input
                id="otpCode"
                name="otpCode"
                type="text"
                required
                maxLength="6"
                className="relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-center text-2xl tracking-widest"
                placeholder="000000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={goBack}
                className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                ← Volver
              </button>
              <button
                type="submit"
                disabled={isLoading || otpCode.length !== 6}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verificando...' : 'Verificar OTP'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
              >
                Reenviar código OTP
              </button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="text-sm text-yellow-700">
                <strong>💡 Consejo:</strong> Revise su bandeja de entrada y carpeta de spam.
                El código expira en 15 minutos.
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-6">
            <LogoOrganizacion className="h-16 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Dashboard Administrativo
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Acceso seguro con verificación OTP
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleCredentialsSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                Usuario
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Usuario"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Contraseña
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Verificando...' : 'Continuar'}
            </button>
          </div>

          <div className="text-center">
            <a
              href="/"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              ← Volver al inicio de sesión de líderes
            </a>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="text-sm text-green-700">
              <strong>🛡️ Seguridad mejorada:</strong><br />
              • Datos enmascarados (emails, documentos)<br />
              • Rate limiting para prevenir ataques<br />
              • Autenticación OTP para usuarios específicos<br />
              • Middleware de seguridad activo
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginDashboardOTP;